import React, { useCallback, useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';

// Custom DOM event the <ExpandCollapseAllControl> dispatches and
// every <CollapsibleSection> listens to. We use a plain DOM event
// (not Context) so the control can sit anywhere in the page tree
// — including the hero, which is rendered inside <PageShell>'s
// hero slot, separately from the page body where the sections
// live. The payload's `prefix` filters which sections respond:
// "telemetry" matches storageKey "telemetry.engagement",
// "telemetry.sales", etc., but doesn't bleed across pages.
const SET_ALL_EVENT = 'collapsible-section:set-all';

/**
 * Broadcast a set-all command to every <CollapsibleSection> whose
 * storageKey starts with the given prefix. Exposed so dashboard
 * pages can wire up "Expand all / Collapse all" controls without
 * cracking the event-name open.
 */
export function setAllCollapsibleSections(prefix, expanded) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(SET_ALL_EVENT, {
    detail: { prefix, expanded },
  }));
}

/**
 * CollapsibleSection — shared dashboard section shell with a
 * persisted expanded/collapsed state.
 *
 * Used to fold away dense dashboard blocks (Engineering health,
 * Sales Intelligence funnels, Weekly OPS triggers, etc.) so admins
 * can focus on the section they're actively investigating without
 * scrolling past dozens of unrelated tiles. Preference is stored
 * in localStorage keyed by `storageKey` so the layout survives
 * page reloads.
 *
 * Props:
 *   storageKey      — required; localStorage key for the persisted
 *                     preference. Use a namespaced string like
 *                     'telemetry.engineering' so different mount
 *                     points don't collide.
 *   eyebrow         — small all-caps tag above the title (e.g.
 *                     "ENGINEERING"). Optional.
 *   title           — main section heading.
 *   description     — optional one-liner shown next to the chevron.
 *   accent          — Tailwind text color class for the eyebrow
 *                     (e.g. 'text-indigo-600'). Defaults to slate.
 *   badge           — optional React node rendered next to the title
 *                     (e.g. an active-incident pill). Visible in
 *                     both states so admins notice it while
 *                     collapsed.
 *   meta            — optional React node rendered to the right of
 *                     the toggle row when collapsed (e.g. "5 tiles").
 *                     Hidden when expanded so the header stays clean.
 *   trailing        — optional React node always shown on the right
 *                     of the header (e.g. a global "Send Today"
 *                     button). Click does NOT toggle the section.
 *   defaultExpanded — initial state when no preference is stored.
 *                     Defaults to false (collapsed) — see
 *                     `defaultIfMissing` if you want a different
 *                     fallback per-section.
 *   testId          — base data-testid; the toggle button gets
 *                     `${testId}-toggle`.
 *   children        — section content; only rendered when expanded
 *                     (mount-on-expand keeps the page light when
 *                     several sections are collapsed at once).
 */
export default function CollapsibleSection({
  storageKey,
  eyebrow,
  title,
  description,
  accent = 'text-slate-600',
  badge = null,
  meta = null,
  trailing = null,
  defaultExpanded = false,
  testId,
  onDark = false,
  children,
}) {
  const [expanded, setExpanded] = useState(() => {
    if (!storageKey) return defaultExpanded;
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved === null) return defaultExpanded;
      return saved === 'true';
    } catch {
      return defaultExpanded;
    }
  });

  const toggle = useCallback(() => {
    setExpanded((prev) => {
      const next = !prev;
      if (storageKey) {
        try { localStorage.setItem(storageKey, String(next)); } catch { /* unavailable */ }
      }
      return next;
    });
  }, [storageKey]);

  // Listen for "set-all" broadcasts from <ExpandCollapseAllControl>.
  // Only respond when the broadcast's prefix matches our storageKey
  // namespace — `telemetry.*`, `lei.*`, `maturity.*` — so a single
  // page can have its own expand-all without affecting siblings.
  useEffect(() => {
    if (!storageKey) return undefined;
    const handler = (e) => {
      const { prefix, expanded: nextExpanded } = e.detail || {};
      if (typeof nextExpanded !== 'boolean') return;
      // Empty prefix = match every section. Otherwise require a
      // dotted prefix match so "lei" doesn't accidentally match
      // "leiSomethingElse".
      const matches = !prefix || storageKey === prefix
        || storageKey.startsWith(`${prefix}.`);
      if (!matches) return;
      setExpanded(nextExpanded);
      try { localStorage.setItem(storageKey, String(nextExpanded)); } catch { /* unavailable */ }
    };
    window.addEventListener(SET_ALL_EVENT, handler);
    return () => window.removeEventListener(SET_ALL_EVENT, handler);
  }, [storageKey]);

  const toggleTestId = testId ? `${testId}-toggle` : 'collapsible-section-toggle';

  return (
    <section
      className="mb-6"
      data-testid={testId}
      data-expanded={expanded ? 'true' : 'false'}
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <button
          type="button"
          onClick={toggle}
          aria-expanded={expanded}
          className="flex-1 min-w-0 flex items-start gap-2 text-left group"
          data-testid={toggleTestId}
        >
          <ChevronDown
            size={16}
            className={
              'mt-1 shrink-0 transition-transform duration-200 '
              + (onDark ? 'text-slate-400 ' : 'text-slate-500 ')
              + (expanded ? 'rotate-0' : '-rotate-90')
            }
          />
          <div className="flex-1 min-w-0">
            {eyebrow && (
              <p className={`text-[10px] uppercase tracking-[0.2em] font-bold ${onDark ? 'text-[#44b6d2]' : accent}`}>
                {eyebrow}
              </p>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className={`text-lg font-bold mt-0.5 leading-tight ${onDark ? 'text-slate-100 group-hover:text-white' : 'text-slate-900 group-hover:text-slate-700'}`}>
                {title}
              </h2>
              {badge}
            </div>
            {expanded && description && (
              <p className={`text-xs mt-1 ${onDark ? 'text-slate-400' : 'text-slate-500'}`}>{description}</p>
            )}
          </div>
        </button>
        <div className="flex items-center gap-2 shrink-0">
          {!expanded && meta}
          {trailing}
        </div>
      </div>
      {expanded && <div data-testid={testId ? `${testId}-body` : undefined}>{children}</div>}
    </section>
  );
}
